/**
 * `AW15b` · El brazo declarado por la RUTA, leído en UN solo lugar.
 *
 * Estas tres declaraciones vivían adentro de `App.tsx` desde `ADR-0136` y las
 * usaba sólo ese archivo. `AW15b` las MUEVE acá enteras —no las copia— porque
 * ahora las necesita una segunda unidad, `padron/tabla.tsx`, y la alternativa
 * era escribir un segundo lector del mismo parámetro de ruta.
 *
 * ⛔ **Un segundo lector es el modo de falla que este archivo existe para
 * impedir.** Dos `useParams()[…]` sobre el mismo segmento no fallan cuando
 * divergen: el que se olvidó de un caso —la cadena vacía, un `undefined`, un
 * nombre de parámetro tipeado distinto— devuelve otra cosa y **nadie se pone
 * rojo**. El comentario original ya lo decía para el par
 * `PARAMETRO_BRAZO`/`SEGMENTO_BRAZO`: «uno derivado del otro para que no puedan
 * divergir». Es la misma disciplina, un nivel más arriba.
 *
 * ⚠️ **Y hay un riesgo medido en EXPORTARLO, que se declara acá.**
 * `configuracion.ts` dejó escrito que «el gate de `AW10` descubre componentes
 * **montando cada export de `src/**`**», y por eso `App.tsx` NO exporta ni
 * `Marco` ni `MetricasDelBrazo`. La medición sobre el árbol, el 26/8/2026:
 *
 *   · `src/**` ya exporta **siete hooks** —`useConfiguracion`,
 *     `useBordeParticipantes`, `useBordeEscritura`, `useBordeConversaciones`,
 *     `useBordeEnvios`, `useBordeExportacion`, `useBordeMetricas`— y los siete
 *     **LANZAN** al montarse fuera de su proveedor («se llamó fuera de
 *     <Proveedor…>»). El gate de `AW10` convive con eso hoy.
 *   · `useBrazo` es **estrictamente menos riesgoso que esos siete**, porque
 *     fuera de un enrutador **no lanza**: `useParams()` de `react-router@7.18.2`
 *     es `React.useContext(RouteContext)` con un contexto cuyo default trae
 *     `matches: []`, así que devuelve `{}` ⇒ `useBrazo()` devuelve `null`
 *     (medido en `node_modules/react-router/dist/development/chunk-62JRHF6Z.mjs:5975`).
 *
 * ⇒ **fuera de una ruta este hook FALLA CERRADO**, que es exactamente lo que la
 * etapa pide de la pantalla: sin brazo declarado, la escritura no se ofrece.
 */

import { useParams } from "react-router"

/**
 * El nombre del parámetro de ruta del brazo, y el segmento que lo declara.
 *
 * Uno derivado del otro para que no puedan divergir: un `path=":brazo"` con un
 * `useParams().arm` no falla, devuelve `undefined` y la pantalla se comporta como
 * si nadie hubiera declarado nada.
 */
export const PARAMETRO_BRAZO = "brazo"
export const SEGMENTO_BRAZO = `:${PARAMETRO_BRAZO}`

/**
 * El brazo declarado por la ruta, o `null` si nadie declaró ninguno.
 *
 * `null` y no un default: **esta aplicación no puede inventar un nombre de
 * brazo.** `Arm = string` y los brazos son `[CONFIG]` por instancia, así que
 * cualquier default sería el nombre de una instancia disfrazado de constante.
 * `null` significa «acá no se declaró ninguno», que es exactamente el
 * comportamiento de antes de `ADR-0136`: el servidor lo resuelve de la sesión.
 *
 * ⭐ **Y para `AW15b` ese `null` no es un detalle de lectura: es la condición de
 * falla cerrada de una ESCRITURA.** Un `""` de brazo lo rechaza
 * `leerCuerpoDeFlag` de `apps/admin-web` con `pedidoMalFormado` —o sea, avisa—;
 * lo que no avisa es un default plausible, que escribiría el flag en el brazo
 * equivocado, o lanzaría un `500` indistinguible de una caída porque el `UPDATE`
 * no tocó ninguna fila. Lo que se hace con `null` es **no ofrecer la escritura y
 * decir por qué**, no adivinar.
 */
export function useBrazo(): string | null {
  const parametros = useParams()
  const brazo = parametros[PARAMETRO_BRAZO]
  return brazo === undefined || brazo === "" ? null : brazo
}
